import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {InputDatepicker} from "../../../../../components/BaseComponents/InputDatepicker";
import {InputTimepicker} from "../../../../../components/BaseComponents/InputTimepicker";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {swal, swalConfirm, swalError, swalLoading, swalSuccess} from "../../../../../helpers/SweetAlertWrapper";

const MAX_ATTACHMENTS = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default class TimeoffRequestsNewRequestPage extends React.Component {
    constructor(props) {
        super(props);
        const defaultStaffId = props.selectedStaffId || this.getDefaultStaffId(props.staffList);
        this.state = {
            id_dipendente: defaultStaffId,
            date: "",
            dateDisplay: "",
            start_time: "",
            end_time: "",
            reason: "",
            attachments: []
        };
    }

    getDefaultStaffId = staffList => {
        if (!staffList || !staffList.length) return null;
        return staffList[0].id_dipendente || null;
    }

    handleStaffChange = e => {
        const id_dipendente = parseInt(e.target.value, 10) || null;
        this.setState({id_dipendente: id_dipendente});
    }

    handleDateChange = e => {
        const selectedDate = e.value;
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const displayDate = moment(selectedDate).format("DD-MM-YYYY");
        this.setState({date: formattedDate, dateDisplay: displayDate});
    }

    handleStartTimeChange = e => {
        const formattedTime = moment(e.value).format("HH:mm");
        this.setState({start_time: formattedTime});
    }

    handleEndTimeChange = e => {
        const formattedTime = moment(e.value).format("HH:mm");
        this.setState({end_time: formattedTime});
    }

    handleReasonChange = e => this.setState({reason: e.target.value});

    handleAttachmentsChange = e => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_ATTACHMENTS) {
            swalError("Puoi caricare al massimo 3 allegati");
            return;
        }
        const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
        if (oversizedFile) {
            swalError("Ogni allegato deve essere massimo di 5MB");
            return;
        }
        this.setState({attachments: files});
    }

    handleRemoveAttachment = index => _ => {
        const attachments = this.state.attachments.filter((_, i) => i !== index);
        this.setState({attachments: attachments});
    }

    validateForm = () => {
        if (!this.state.id_dipendente) {
            swalError("Specifica un collaboratore valido");
            return false;
        }
        if (!this.state.date || !this.state.start_time) {
            swalError("Specifica una data e un orario di inizio validi");
            return false;
        }
        if (!this.state.end_time) {
            swalError("Specifica una data e un orario di fine validi");
            return false;
        }
        if (!this.state.reason) {
            swalError("Specifica una motivazione valida");
            return false;
        }
        return true;
    }

    handleSubmit = _ => {
        if (!this.validateForm()) return;
        const payload = {
            id_dipendente: this.state.id_dipendente,
            date: this.state.date,
            start_time: this.state.start_time,
            end_time: this.state.end_time,
            reason: this.state.reason
        };

        const saveRequest = () => {
            swalLoading();
            ModuloHRAPI.TimeoffRequests.save(payload, this.state.attachments)
                .then(_ => {
                    swal.safeClose();
                    swalSuccess("Richiesta inviata");
                    if (this.props.onRequestCreated) {
                        this.props.onRequestCreated(this.state.id_dipendente);
                    }
                })
                .catch(error => {
                    swal.safeClose();
                    swalError(error || "Non e' stato possibile inviare la richiesta");
                });
        };

        swalLoading();
        ModuloHRAPI.TimeoffRequests.checkLockedPeriod(payload.id_dipendente, payload.date)
            .then(_ => {
                saveRequest();
            })
            .catch(r => {
                if (r.status === 'CONFIRM') {
                    swalConfirm(`${r.message}<br/>Vuoi proseguire comunque?`).then(_ => saveRequest())
                } else {
                    swalError(r.message)
                }
            });
    }

    render() {
        return (
            <Fragment>
                <Page
                    title={"Nuova richiesta"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={
                        <div className={"container py-3"}>
                            <div className={"form-group"}>
                                <label>Collaboratore</label>
                                <select
                                    className={"form-control"}
                                    value={this.state.id_dipendente || ""}
                                    onChange={this.handleStaffChange}
                                >
                                    {
                                        (this.props.staffList || []).map(staff => {
                                            const label = staff.negozio?.nome ? `${staff.nome} - ${staff.negozio.nome}` : staff.nome;
                                            return (
                                                <option key={staff.id_dipendente}
                                                        value={staff.id_dipendente}>{label}</option>
                                            );
                                        })
                                    }
                                </select>
                            </div>

                            <div className={"form-group"}>
                                <label>Data</label>
                                <InputDatepicker
                                    className={"form-control bg-main-z3"}
                                    value={this.state.dateDisplay}
                                    onChange={this.handleDateChange}
                                />
                            </div>

                            <div className={"form-row"}>
                                <div className={"form-group col-6"}>
                                    <label>Ora inizio</label>
                                    <InputTimepicker
                                        className={"form-control bg-main-z3"}
                                        value={this.state.start_time}
                                        onChange={this.handleStartTimeChange}
                                        startDate={moment().set('minutes', 0).toDate()}
                                    />
                                </div>
                                <div className={"form-group col-6"}>
                                    <label>Ora fine</label>
                                    <InputTimepicker
                                        className={"form-control bg-main-z3"}
                                        value={this.state.end_time}
                                        onChange={this.handleEndTimeChange}
                                        startDate={moment().add(1, 'hour').set('minutes', 0).toDate()}
                                    />
                                </div>
                            </div>

                            <div className={"form-group"}>
                                <label>Motivazione</label>
                                <textarea
                                    className={"form-control"}
                                    rows={3}
                                    value={this.state.reason}
                                    onChange={this.handleReasonChange}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Allegati (max 3)</label>
                                <input
                                    type={"file"}
                                    multiple={true}
                                    className={"form-control-file"}
                                    onChange={this.handleAttachmentsChange}
                                />
                                {
                                    this.state.attachments.length > 0 &&
                                    <ul className={"list-group mt-2"}>
                                        {
                                            this.state.attachments.map((file, index) => {
                                                return (
                                                    <li key={index}
                                                        className={"list-group-item d-flex justify-content-between align-items-center"}>
                                                        <span className={"small"}>{file.name}</span>
                                                        <button
                                                            type={"button"}
                                                            className={"btn btn-sm btn-outline-secondary"}
                                                            onClick={this.handleRemoveAttachment(index)}
                                                        >
                                                            Rimuovi
                                                        </button>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                }
                            </div>

                            <div className={"text-right"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-primary"}
                                    onClick={this.handleSubmit}
                                >
                                    Invia richiesta
                                </button>
                            </div>
                        </div>
                    }
                />
            </Fragment>
        );
    }
}

TimeoffRequestsNewRequestPage.propTypes = {
    active: PropTypes.bool,
    onDismiss: PropTypes.func,
    onRequestCreated: PropTypes.func,
    staffList: PropTypes.array,
    selectedStaffId: PropTypes.number
};
